<script setup>
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { fetchExport } from '@/api/client.js';
import Banner from '@/components/ds/Banner.vue';
import Button from '@/components/ds/Button.vue';
import Icon from '@/components/ds/Icon.vue';
import TimeWindowSelector from '@/components/ds/TimeWindowSelector.vue';
import AnomaliesPanel from '@/components/panels/AnomaliesPanel.vue';
import AudiencePanel from '@/components/panels/AudiencePanel.vue';
import CustomEventsPanel from '@/components/panels/CustomEventsPanel.vue';
import DashboardGroup from '@/components/panels/DashboardGroup.vue';
import DeadClickPanel from '@/components/panels/DeadClickPanel.vue';
import DeliveryPanel from '@/components/panels/DeliveryPanel.vue';
import ErrorGroupsPanel from '@/components/panels/ErrorGroupsPanel.vue';
import HostHealthPanel from '@/components/panels/HostHealthPanel.vue';
import IncidentCandidatesPanel from '@/components/panels/IncidentCandidatesPanel.vue';
import ModuleUsagePanel from '@/components/panels/ModuleUsagePanel.vue';
import PageviewsPanel from '@/components/panels/PageviewsPanel.vue';
import PulsePanel from '@/components/panels/PulsePanel.vue';
import RageHotspotPanel from '@/components/panels/RageHotspotPanel.vue';
import ReferrersPanel from '@/components/panels/ReferrersPanel.vue';
import ServiceHealthPanel from '@/components/panels/ServiceHealthPanel.vue';
import SummaryKpisPanel from '@/components/panels/SummaryKpisPanel.vue';
import SyntheticResultsPanel from '@/components/panels/SyntheticResultsPanel.vue';
import TechPanel from '@/components/panels/TechPanel.vue';
import TopPagesPanel from '@/components/panels/TopPagesPanel.vue';
import { useTenantTimezone } from '@/composables/useTenantTimezone.js';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';

defineOptions({ name: 'LandingView' });

const auth = useAuthStore();
const tenant = useTenantStore();
const route = useRoute();
const { tenants } = storeToRefs(auth);
const linkedAppId = computed(() => {
  const appId = route.query.appId;
  return typeof appId === 'string' && tenants.value.some(t => t.app_id === appId) ? appId : null;
});
const linkedErrorFingerprint = computed(() => {
  const fingerprint = route.query.errorFingerprint;
  return linkedAppId.value === tenant.activeAppId && typeof fingerprint === 'string'
    && /^[a-f0-9]{64}$/.test(fingerprint) ? fingerprint : '';
});
watch(linkedAppId, appId => {
  if (appId) tenant.setActiveTenant(appId);
}, { immediate: true });

const activeTenant = computed(
  () => tenants.value.find((t) => t.app_id === tenant.activeAppId) ?? tenants.value[0] ?? null
);

const { timezone } = useTenantTimezone(activeTenant);

const tenantHeader = computed(() => {
  const t = activeTenant.value;
  if (!t) return '—';
  const name = t.name || t.app_id;
  return `${name} — ${timezone.value}`;
});

// Export CSV de los datasets tabulares. Descarga vía fetch+blob (la
// respuesta es text/csv, no JSON).
const EXPORT_DATASETS = [
  { value: 'top-pages', label: 'Top pages' },
  { value: 'referrers', label: 'Traffic sources' },
  { value: 'audience', label: 'Audience' },
  { value: 'custom-events', label: 'Custom events' },
];
const exportDataset = ref('top-pages');
const exporting = ref(false);
const exportError = ref('');

async function downloadExport() {
  if (!tenant.activeAppId || exporting.value) return;
  exporting.value = true;
  exportError.value = '';
  try {
    const { blob, filename } = await fetchExport({
      dataset: exportDataset.value, appId: tenant.activeAppId, window: tenant.timeWindow,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    exportError.value = 'Export failed. Try again.';
  } finally {
    exporting.value = false;
  }
}

// Story 2.13 + 2.15 — panels independientes; el Banner global aparece
// SOLO si TODOS están en `error` (un endpoint degradado no debe
// disparar el banner). Los insight panels se suman a la misma regla.
const PANEL_NAMES = Object.freeze([
  'summary', 'serviceHealth', 'errorGroups', 'delivery', 'anomalies', 'pulse', 'rage', 'dead', 'pageviews',
  'topPages', 'referrers', 'tech', 'audience', 'customEvents',
  'moduleUsage', 'syntheticResults', 'hostHealth',
]);

const panelRefs = ref(Object.fromEntries(PANEL_NAMES.map((n) => [n, null])));

function bind(name) {
  return (el) => { panelRefs.value[name] = el; };
}

const errorCount = computed(() => {
  let count = 0;
  for (const name of PANEL_NAMES) {
    const el = panelRefs.value[name];
    if (el?.ps?.state?.value === 'error') count++;
  }
  return count;
});

const showGlobalError = computed(() => errorCount.value === PANEL_NAMES.length);
const degradedPanels = computed(() => PANEL_NAMES.filter((name) =>
  panelRefs.value[name]?.refreshStatus?.degraded));

const DEFAULT_GROUPS = Object.freeze({ traffic: true, experience: true, audience: true });
const groupOpen = reactive({ ...DEFAULT_GROUPS });
const groupStorageKey = computed(() => {
  const user = auth.user?.email || 'anonymous';
  const app = tenant.activeAppId || 'none';
  return `otera.dashboard.groups.${user}.${app}`;
});

function loadGroupPreferences() {
  Object.assign(groupOpen, DEFAULT_GROUPS);
  try {
    const saved = JSON.parse(localStorage.getItem(groupStorageKey.value) || 'null');
    if (saved && typeof saved === 'object') {
      for (const key of Object.keys(DEFAULT_GROUPS)) {
        if (typeof saved[key] === 'boolean') groupOpen[key] = saved[key];
      }
    }
  } catch { /* Storage is an enhancement; disclosures stay open without it. */ }
}

function onGroupToggle(key, event) {
  groupOpen[key] = event.currentTarget.open;
  try { localStorage.setItem(groupStorageKey.value, JSON.stringify(groupOpen)); } catch { /* noop */ }
}

watch(groupStorageKey, loadGroupPreferences, { immediate: true });

function retryAll() {
  for (const name of PANEL_NAMES) {
    panelRefs.value[name]?.ps?.retry?.();
  }
}

function retryAffected() {
  for (const name of degradedPanels.value) {
    panelRefs.value[name]?.ps?.retry?.();
  }
}
</script>

<template>
  <section class="landing">
    <header class="landing__header">
      <div class="landing__intro">
        <div>
          <p class="landing__eyebrow">
            Workspace analytics
          </p>
          <h1 class="landing__title">
            Dashboard
          </h1>
          <p class="landing__description">
            Understand your traffic, spot friction, and keep an eye on reliability.
          </p>
        </div>
        <p class="landing__tenant">
          <Icon name="layers" />
          <span>{{ tenantHeader }}</span>
        </p>
      </div>
      <div class="landing__header-controls">
        <div class="landing__period">
          <span class="landing__control-label">Reporting period</span>
          <TimeWindowSelector />
        </div>
        <div class="landing__export">
          <label
            class="landing__control-label"
            for="export-dataset"
          >Export report</label>
          <div class="landing__export-actions">
            <select
              id="export-dataset"
              v-model="exportDataset"
              class="landing__export-select"
              :disabled="exporting"
            >
              <option
                v-for="d in EXPORT_DATASETS"
                :key="d.value"
                :value="d.value"
              >
                {{ d.label }}
              </option>
            </select>
            <Button
              variant="secondary"
              size="sm"
              icon-left="download"
              :disabled="exporting"
              @click="downloadExport"
            >
              {{ exporting ? 'Exporting…' : 'Export CSV' }}
            </Button>
          </div>
        </div>
      </div>
    </header>
    <p
      v-if="exportError"
      class="landing__export-error"
      role="alert"
    >
      {{ exportError }}
    </p>

    <Banner
      v-if="showGlobalError"
      tone="danger"
      class="landing__global-error"
      title="The dashboard is having trouble"
    >
      Retry one of the panels or refresh the page.
      <template #action>
        <Button
          variant="secondary"
          size="sm"
          icon-left="refresh"
          @click="retryAll"
        >
          Retry all
        </Button>
      </template>
    </Banner>
    <Banner
      v-if="degradedPanels.length"
      tone="warning"
      role="status"
      aria-live="polite"
      class="landing__global-error"
      title="Some dashboard data could not refresh"
    >
      {{ degradedPanels.length }} {{ degradedPanels.length === 1 ? 'panel is' : 'panels are' }} showing the last successful data.
      <template #action>
        <Button
          variant="secondary"
          size="sm"
          icon-left="refresh"
          @click="retryAffected"
        >
          Retry affected
        </Button>
      </template>
    </Banner>

    <div class="landing__grid landing__primary">
      <SummaryKpisPanel
        :ref="bind('summary')"
        class="landing__full"
      />
      <PageviewsPanel :ref="bind('pageviews')" />
      <PulsePanel :ref="bind('pulse')" />
    </div>

    <section
      class="landing__operations"
      aria-labelledby="operations-title"
    >
      <header class="landing__section-header">
        <span class="landing__section-icon"><Icon
          name="shield"
          :size="20"
        /></span>
        <div>
          <h2
            id="operations-title"
            class="landing__section-title"
          >
            Service &amp; reliability
          </h2>
          <p class="landing__description">
            Monitor service health, investigate errors, and review delivery.
          </p>
        </div>
      </header>
      <div class="landing__grid">
        <ServiceHealthPanel
          :ref="bind('serviceHealth')"
          class="landing__full"
        />
        <SyntheticResultsPanel
          :ref="bind('syntheticResults')"
          class="landing__full"
        />
        <HostHealthPanel
          :ref="bind('hostHealth')"
          class="landing__full"
        />
        <IncidentCandidatesPanel class="landing__full" />
        <AnomaliesPanel
          :ref="bind('anomalies')"
          class="landing__full"
        />
        <ErrorGroupsPanel
          :ref="bind('errorGroups')"
          :focus-fingerprint="linkedErrorFingerprint"
          class="landing__full"
        />
        <DeliveryPanel
          :ref="bind('delivery')"
          class="landing__full"
        />
      </div>
    </section>

    <div class="landing__groups">
      <DashboardGroup
        title="Traffic"
        description="Discover your most visited pages and where visitors come from."
        icon="arrowUp"
        :open="groupOpen.traffic"
        @toggle="onGroupToggle('traffic', $event)"
      >
        <div class="landing__grid">
          <TopPagesPanel :ref="bind('topPages')" />
          <ReferrersPanel :ref="bind('referrers')" />
        </div>
      </DashboardGroup>
      <DashboardGroup
        title="User experience"
        description="Explore module usage, interaction friction, and custom events."
        icon="activity"
        :open="groupOpen.experience"
        @toggle="onGroupToggle('experience', $event)"
      >
        <div class="landing__grid">
          <ModuleUsagePanel
            :ref="bind('moduleUsage')"
            class="landing__full"
          />
          <RageHotspotPanel :ref="bind('rage')" />
          <DeadClickPanel :ref="bind('dead')" />
          <CustomEventsPanel
            :ref="bind('customEvents')"
            class="landing__full"
          />
        </div>
      </DashboardGroup>
      <DashboardGroup
        title="Audience &amp; technology"
        description="Get to know your audience and the environments they use."
        icon="user"
        :open="groupOpen.audience"
        @toggle="onGroupToggle('audience', $event)"
      >
        <div class="landing__grid">
          <TechPanel :ref="bind('tech')" />
          <AudiencePanel :ref="bind('audience')" />
        </div>
      </DashboardGroup>
    </div>
  </section>
</template>

<style scoped>
.landing {
  max-width: 90rem;
  margin: 0 auto;
}

.landing :deep(.panel) {
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-sm);
}

.landing__header {
  margin-bottom: var(--space-6);
}

.landing__intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.landing__eyebrow {
  margin: 0 0 var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--fw-medium);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--fg-secondary);
}

.landing__title {
  margin: 0 0 var(--space-2);
  font-size: var(--text-4xl);
}

.landing__description {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--fg-secondary);
}

.landing__tenant {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--bg-canvas);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  color: var(--fg-secondary);
  margin: 0;
  overflow-wrap: anywhere;
}

.landing__tenant svg {
  flex-shrink: 0;
}

.landing__header-controls {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-4);
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--bg-canvas);
}

.landing__period,
.landing__export {
  display: grid;
  gap: var(--space-2);
  min-width: 0;
}

.landing__control-label {
  font-size: var(--text-xs);
  font-weight: var(--fw-medium);
  color: var(--fg-secondary);
}


.landing__export-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.landing__export-select {
  font: inherit;
  font-size: var(--text-sm);
  padding: var(--space-1) var(--space-2);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  cursor: pointer;
  min-height: var(--control-md);
  min-width: 0;
  max-width: 100%;
}

.landing__export-select:disabled {
  opacity: 0.5;
  cursor: default;
}

.landing__export-error {
  margin: 0 0 var(--space-4);
  font-size: var(--text-sm);
  color: var(--fg-danger, var(--danger-default));
}

.landing__global-error {
  margin-bottom: var(--space-5);
}

.landing__grid {
  display: grid;
  gap: var(--space-6);
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.landing__grid > * {
  min-width: 0;
}

.landing__primary {
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  margin-bottom: var(--space-6);
}

.landing__operations {
  margin-bottom: var(--space-7);
  padding: var(--space-5);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--bg-subtle);
}

.landing__section-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
}

.landing__section-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: var(--control-lg);
  height: var(--control-lg);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-lg);
  color: var(--accent-fg-on-soft);
  background: var(--bg-canvas);
}

.landing__section-title {
  margin: 0 0 var(--space-1);
  font-size: var(--text-lg);
}

.landing__groups {
  display: grid;
  gap: var(--space-5);
}

/* KPI strip y custom events ocupan el ancho completo del grid. */
.landing__full {
  grid-column: 1 / -1;
}

@media (width <= 1023px) {
  .landing__grid {
    grid-template-columns: 1fr;
    gap: var(--space-5);
  }
}

@media (width <= 639px) {
  .landing__intro {
    flex-direction: column;
  }

  .landing__header-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .landing__operations {
    padding: var(--space-3);
  }

  .landing__section-header {
    padding: var(--space-1);
  }
}
</style>
